import React, { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Grid3x3, Bookmark, Star, Settings as SettingsIcon, ChevronLeft, ShieldAlert, Waves, LogIn, UserCircle2, ChevronDown, CreditCard, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useStackMode } from "@/lib/stackModeContext";
import { useEntitlement } from "@/lib/entitlement";
import Footer from "@/components/Footer";
import OfflineBanner from "@/components/OfflineBanner";
import ActiveOperationPill from "@/components/rce/ActiveOperationPill"; // eslint-disable-line no-unused-vars -- header pill removed in Dashboard V2 (Rig Engineering card opens the switcher)
import MigrationBanner from "@/components/rce/MigrationBanner";
import { signOutLocally } from "@/lib/authState";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { to: "/home", label: "Home", icon: LayoutDashboard, testid: "nav-home" },
  { to: "/categories", label: "Categories", icon: Grid3x3, testid: "nav-categories" },
  { to: "/favorites", label: "Favorites", icon: Star, testid: "nav-favorites" },
  { to: "/saved", label: "Saved", icon: Bookmark, testid: "nav-saved" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

// Map backend entitlement to a user-facing plan label. Phase A single-tier
// model: ONE product (RigSentinel Pro) with two cadences (Monthly/Annual).
// Internal tier names (`field_pro`, `professional`) MUST NEVER leak to the UI.
import { formatPlanLabel, formatStatusLabel } from "../lib/featureAccess";

export default function Layout() {
  const loc = useLocation();
  const navigate = useNavigate();
  const showBack = !["/home", "/categories", "/favorites", "/saved", "/settings"].includes(loc.pathname);
  // Five-Defect Correction Pass, Defect 3 (mobile residual) — pages that
  // render the bottom-right FloatingActionToolbar need extra right margin
  // on narrow phones so the FAB's own column never overlaps the content
  // column (content here spans nearly edge-to-edge at 390px). Positioning
  // only — does not touch any other route's layout.
  const FAB_ROUTES = [
    /^\/rig-sheets\//,
    /^\/wells\/new$/,
    /^\/wells\/[^/]+$/,
    /^\/wells\/[^/]+\/(bha|drill-strings|kill-sheets)\//,
    /^\/reports\/daily-drilling-report/,
  ];
  const hasFab = FAB_ROUTES.some((re) => re.test(loc.pathname));
  const { stackMode, setStackMode } = useStackMode();
  const ent = useEntitlement();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);

  // Close popover on outside click + Escape
  useEffect(() => {
    if (!accountMenuOpen) return;
    const onDocClick = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setAccountMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setAccountMenuOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [accountMenuOpen]);

  const onSignOut = async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch (_) { /* best-effort: cookie still cleared on next request */ }
    signOutLocally();
    setAccountMenuOpen(false);
    toast.success("Signed out");
    // Hard reload so the entitlement hook + header re-render from scratch.
    window.location.assign("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--rs-bg)] text-[var(--rs-text)]">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md border-b"
        style={{
          background: "rgba(10,11,14,0.92)",
          borderColor: "var(--rs-border)",
          // iOS standalone-PWA safe-area fix — the installed iPhone/iPad
          // header sits under the status bar/notch without this; adds to
          // (not instead of) the header's normal vertical spacing below.
          // env() resolves to 0px on every non-notched/non-PWA context, so
          // this is a no-op everywhere else (desktop, Android, browser tab).
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
        data-testid="app-header"
      >
        <div className="max-w-3xl mx-auto px-4 py-2 min-h-14 flex items-center gap-3">
          {showBack ? (
            <button
              data-testid="back-btn"
              onClick={() => navigate(-1)}
              className="btn-ghost h-9 w-9 rounded-md inline-flex items-center justify-center"
              aria-label="Back"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          ) : (
            <div className="h-9 w-9 rounded-md flex items-center justify-center" style={{ background: "var(--rs-amber)" }}>
              <span className="font-heading font-black text-[var(--rs-bg)] text-sm tracking-tight">RS</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-heading font-black text-lg leading-tight tracking-tight truncate">RigSentinel</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--rs-text-3)]">
              <span className="hidden sm:inline">Field-Ready · Deterministic · Operating mode:{" "}</span>
              <span style={{ color: stackMode === "subsea" ? "#60A5FA" : "var(--rs-amber)" }}>
                {stackMode === "subsea" ? "Subsea" : "Surface"}
              </span>
            </div>
          </div>
          {ent.loading ? null : ent.authenticated ? (
            <div className="relative shrink-0" ref={accountMenuRef}>
              <button
                data-testid="header-account-indicator"
                onClick={() => setAccountMenuOpen((v) => !v)}
                aria-expanded={accountMenuOpen}
                aria-haspopup="menu"
                className="btn-ghost h-9 px-2.5 rounded-md inline-flex items-center gap-1.5 text-[11px] label-up max-w-[12rem]"
                title={ent.email ? `Signed in as ${ent.email}` : "Signed in"}
              >
                <UserCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--rs-amber)" }} />
                <span className="truncate" data-testid="header-account-email">{ent.email || "Account"}</span>
                <ChevronDown className="w-3 h-3 shrink-0 opacity-70" />
              </button>
              {accountMenuOpen && (
                <div
                  data-testid="header-account-menu"
                  role="menu"
                  className="absolute right-0 mt-2 w-[240px] rounded-md border border-[var(--rs-border)] shadow-xl z-40"
                  style={{ background: "rgba(16,18,22,0.98)", backdropFilter: "blur(6px)" }}
                >
                  <div className="px-3 py-2.5 border-b border-[var(--rs-border)]">
                    <div className="label-up text-[9.5px] text-[var(--rs-text-3)]">Signed in as</div>
                    <div
                      className="text-[12px] text-[var(--rs-text-1)] truncate mt-0.5"
                      data-testid="header-account-menu-email"
                      title={ent.email}
                    >
                      {ent.email}
                    </div>
                  </div>
                  <div
                    className="px-3 py-2.5 border-b border-[var(--rs-border)]"
                    data-testid="header-account-menu-plan-section"
                  >
                    <div className="label-up text-[9.5px] text-[var(--rs-text-3)]">Current plan</div>
                    {ent.loadError ? (
                      <div
                        className="text-[12px] text-[var(--rs-text-1)] mt-0.5"
                        data-testid="header-account-menu-plan-label"
                      >
                        Unable to load
                      </div>
                    ) : (
                      <>
                        <div
                          className="text-[12px] text-[var(--rs-text-1)] mt-0.5"
                          data-testid="header-account-menu-plan-label"
                        >
                          {formatPlanLabel(ent)}
                        </div>
                        {formatStatusLabel(ent) && (
                          <div
                            className="text-[10.5px] text-[var(--rs-text-3)] mt-0.5"
                            data-testid="header-account-menu-plan-status"
                          >
                            Status: <span style={{ color: "var(--rs-text-2)" }}>{formatStatusLabel(ent)}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <button
                    role="menuitem"
                    data-testid="header-account-menu-account"
                    onClick={() => { setAccountMenuOpen(false); navigate("/account"); }}
                    className="w-full text-left px-3 py-2.5 text-[12px] inline-flex items-center gap-2 text-[var(--rs-text-2)] hover:bg-[rgba(255,179,0,0.08)]"
                  >
                    <UserCircle2 className="w-3.5 h-3.5" style={{ color: "var(--rs-amber)" }} /> Account &amp; password
                  </button>
                  <button
                    role="menuitem"
                    data-testid="header-account-menu-manage-plan"
                    onClick={() => { setAccountMenuOpen(false); navigate("/pricing"); }}
                    className="w-full text-left px-3 py-2.5 text-[12px] inline-flex items-center gap-2 text-[var(--rs-text-2)] hover:bg-[rgba(255,179,0,0.08)]"
                  >
                    <CreditCard className="w-3.5 h-3.5" style={{ color: "var(--rs-amber)" }} /> Manage plan
                  </button>
                  {ent?.isOwner && (
                    <button
                      role="menuitem"
                      data-testid="header-account-menu-owner-dashboard"
                      onClick={() => { setAccountMenuOpen(false); navigate("/admin/owner-dashboard"); }}
                      className="w-full text-left px-3 py-2.5 text-[12px] inline-flex items-center gap-2 text-[var(--rs-text-2)] hover:bg-[rgba(255,179,0,0.08)]"
                    >
                      <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--rs-amber)" }} /> Owner Dashboard
                    </button>
                  )}
                  <button
                    role="menuitem"
                    data-testid="header-account-menu-signout"
                    onClick={onSignOut}
                    className="w-full text-left px-3 py-2.5 text-[12px] inline-flex items-center gap-2 text-[var(--rs-text-2)] hover:bg-[rgba(239,68,68,0.10)] border-t border-[var(--rs-border)]"
                  >
                    <LogOut className="w-3.5 h-3.5" style={{ color: "rgb(252,165,165)" }} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              data-testid="header-signin-btn"
              onClick={() => navigate(`/auth?returnTo=${encodeURIComponent(loc.pathname || "/")}`)}
              className="btn-ghost h-9 px-2.5 rounded-md inline-flex items-center gap-1.5 text-[11px] label-up shrink-0"
              title="Sign in with your email"
            >
              <LogIn className="w-3.5 h-3.5" /> Sign in
            </button>
          )}
        </div>

        {/* Global Surface/Subsea segmented switch — visible on every page */}
        <div className="max-w-3xl mx-auto px-4 pb-2.5">
          <div
            data-testid="global-stack-mode-toggle"
            role="group"
            aria-label="Operating mode"
            className="grid grid-cols-2 rounded-md p-0.5 border"
            style={{
              borderColor: "var(--rs-border)",
              background: "rgba(22,24,29,0.65)",
            }}
          >
            <button
              data-testid="global-stack-surface"
              onClick={() => setStackMode("surface")}
              aria-pressed={stackMode === "surface"}
              className="flex items-center justify-center gap-2 h-9 rounded-[6px] text-xs font-semibold tracking-[0.12em] uppercase transition-colors"
              style={
                stackMode === "surface"
                  ? { background: "rgba(255,179,0,0.18)", color: "var(--rs-amber)", boxShadow: "inset 0 0 0 1px rgba(255,179,0,0.5)" }
                  : { color: "var(--rs-text-3)" }
              }
            >
              <ShieldAlert className="w-3.5 h-3.5" /> Surface
            </button>
            <button
              data-testid="global-stack-subsea"
              onClick={() => setStackMode("subsea")}
              aria-pressed={stackMode === "subsea"}
              className="flex items-center justify-center gap-2 h-9 rounded-[6px] text-xs font-semibold tracking-[0.12em] uppercase transition-colors"
              style={
                stackMode === "subsea"
                  ? { background: "rgba(33,150,243,0.18)", color: "#60A5FA", boxShadow: "inset 0 0 0 1px rgba(33,150,243,0.55)" }
                  : { color: "var(--rs-text-3)" }
              }
            >
              <Waves className="w-3.5 h-3.5" /> Subsea
            </button>
          </div>
        </div>
      </header>

      <OfflineBanner />
      {/* RCE — Phase 1: Non-blocking Project Profile migration banner */}
      <MigrationBanner />

      <main className={`flex-1 w-full max-w-3xl mx-auto pl-4 pt-4 pb-28 ${hasFab ? "pr-16 sm:pr-4" : "pr-4"}`}>
        <Outlet />
        <Footer />
      </main>

      {/* Bottom navigation */}
      <nav
        data-testid="bottom-nav"
        className="fixed bottom-0 left-0 right-0 backdrop-blur-md border-t pb-safe"
        style={{ background: "rgba(22,24,29,0.95)", borderColor: "var(--rs-border)", zIndex: 10000 }}
      >
        <div className="max-w-3xl mx-auto grid grid-cols-5">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] tracking-[0.14em] uppercase font-semibold transition-colors ${
                  isActive ? "text-[var(--rs-amber)]" : "text-[var(--rs-text-3)] hover:text-[var(--rs-text)]"
                }`
              }
            >
              <Icon className="w-5 h-5" strokeWidth={2.25} />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
